import { useMemo, useState } from "react";
import { useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import { useAuth, isStaff } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ShoppingBag, Loader2, Info, ArrowLeft, MessageSquare, Mail } from "lucide-react";
import { api, auth as authStore, errMsg } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";

export default function Login() {
  const { login, setUser } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const redirect = params.get("redirect");

  const [tab, setTab] = useState("signin"); // signin | signup | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const dest = useMemo(() => {
    if (redirect) return redirect;
    if (loc.state?.from && loc.state.from !== "/login") return loc.state.from;
    return null;
  }, [redirect, loc.state]);

  const routeAfterAuth = (u) => {
    if (dest) return nav(dest, { replace: true });
    if (u.role === "DELIVERY_PARTNER" || u.role === "DELIVERY_MANAGER") return nav("/delivery", { replace: true });
    if (isStaff(u)) return nav("/admin", { replace: true });
    nav("/", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      routeAfterAuth(u);
    } catch (err) {
      toast.error(errMsg(err, "Sign-in failed"));
    } finally { setBusy(false); }
  };

  const signup = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const storeId = "6a5204746b57df040a374d6e"; // single-store MVP; multi-tenant picker deferred
      const body = { email: email.trim(), password, name, store_id: storeId };
      if (phone) body.phone = phone;
      const { data } = await api.post("/auth/register", body);
      authStore.setSession(data);
      setUser(data.user);
      toast.success(`Welcome, ${data.user.name || "friend"} — you're in!`);
      routeAfterAuth(data.user);
    } catch (err) {
      toast.error(errMsg(err, "Sign-up failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-primary via-primary to-[hsl(var(--brand-red-deep))] text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, white 2px, transparent 2px), radial-gradient(circle at 80% 60%, white 1px, transparent 1px)",
          backgroundSize: "60px 60px, 40px 40px",
        }} />
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-3 bg-white/10 backdrop-blur rounded-2xl p-3 pr-4">
            <BrandLogo size={36} mono className="text-white [--brand-red:0_0%_100%]" />
          </Link>
        </div>
        <div className="relative">
          <div className="text-5xl font-display font-semibold leading-tight max-w-md">
            Groceries at your doorstep, from a shop you can <span className="italic">trust</span>.
          </div>
          <div className="mt-4 text-primary-foreground/80 text-lg italic font-display underline underline-offset-2">
            Get More, Pay Less!!
          </div>
          <div className="mt-8 flex gap-2 flex-wrap">
            {["30-min delivery", "GST invoices", "Cash on delivery", "Hyperlocal"].map((t) => (
              <span key={t} className="text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/10 backdrop-blur">{t}</span>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-primary-foreground/60">© 2026 BAMKO Retail Pvt Ltd</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-16">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-6 lg:mb-8">
            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-shop">
              <ArrowLeft className="w-4 h-4" /> Back to shop
            </Link>
            <div className="lg:hidden flex items-center">
              <BrandLogo size={26} />
            </div>
          </div>

          <h1 className="text-4xl font-display font-semibold">Welcome</h1>
          <p className="text-muted-foreground mt-1">Sign in to place orders, track deliveries and save your favourites.</p>

          <Card className="mt-6 card-soft border-transparent">
            <CardContent className="p-4 md:p-6">
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="grid grid-cols-3 w-full">
                  <TabsTrigger value="signin" data-testid="tab-signin">Sign in</TabsTrigger>
                  <TabsTrigger value="signup" data-testid="tab-signup">Sign up</TabsTrigger>
                  <TabsTrigger value="otp" data-testid="tab-otp">OTP</TabsTrigger>
                </TabsList>

                <TabsContent value="signin" className="mt-5">
                  <form onSubmit={submit} className="space-y-4">
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5" data-testid="login-email" required />
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label htmlFor="password">Password</Label>
                        <Link to="/forgot-password" className="text-xs text-primary hover:underline font-medium" data-testid="login-forgot-link">Forgot password?</Link>
                      </div>
                      <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" data-testid="login-password" required />
                    </div>
                    <Button type="submit" className="w-full btn-primary-glow" disabled={busy} data-testid="login-submit">
                      {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Sign in
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="signup" className="mt-5">
                  <form onSubmit={signup} className="space-y-3">
                    <div>
                      <Label htmlFor="su-name">Full name</Label>
                      <Input id="su-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="mt-1.5" data-testid="signup-name" required />
                    </div>
                    <div>
                      <Label htmlFor="su-email">Email</Label>
                      <Input id="su-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-1.5" data-testid="signup-email" required />
                    </div>
                    <div>
                      <Label htmlFor="su-phone">Phone</Label>
                      <Input id="su-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91…" className="mt-1.5" data-testid="signup-phone" />
                    </div>
                    <div>
                      <Label htmlFor="su-password">Password</Label>
                      <Input id="su-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="min 8 chars, letters + digits" className="mt-1.5" data-testid="signup-password" required />
                    </div>
                    <Button type="submit" className="w-full btn-primary-glow" disabled={busy} data-testid="signup-submit">
                      {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create account
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="otp" className="mt-5">
                  <div className="rounded-xl border border-dashed p-4 flex gap-3 text-sm text-muted-foreground">
                    <Info className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium text-foreground">SMS OTP coming soon.</div>
                      <div className="mt-1">For now use email & password. We'll enable OTP login when SMS delivery goes live.</div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-3 opacity-70">
                    <div>
                      <Label>Phone number</Label>
                      <Input placeholder="+91…" disabled data-testid="otp-phone" />
                    </div>
                    <Button className="w-full" disabled data-testid="otp-send">
                      <MessageSquare className="w-4 h-4 mr-2" /> Send OTP (disabled)
                    </Button>
                    <div className="text-center text-xs text-muted-foreground">or</div>
                    <Button variant="outline" className="w-full" onClick={() => setTab("signin")} data-testid="otp-use-password">
                      <Mail className="w-4 h-4 mr-2" /> Use email & password
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <div className="text-xs text-muted-foreground mt-6">
            Are you BAMKO staff? Use the same form — you'll be routed to the admin console after sign-in.
          </div>
        </div>
      </div>
    </div>
  );
}
